import { useEffect, useRef, useState } from "react";
import { Slider } from "@/components/ui/slider";

interface Props {
  label: string;
  value: number;
  min: number;
  max: number;          // slider visual max — number input may exceed
  step: number;
  onChange: (v: number) => void;
  /** Optional prefix/suffix shown inside the number field (e.g. "$", "%"). */
  prefix?: string;
  suffix?: string;
  /** Hard ceiling for typed values (defaults to max * 100). */
  hardMax?: number;
  /** Number of fraction digits to allow (defaults to 0). */
  decimals?: number;
}

/**
 * Slider + numeric input bound to the same value.
 * - Slider lets users explore quickly.
 * - Number field lets them type exact values, including beyond the slider max.
 * - touch-action: pan-y keeps vertical page scroll working on iOS/Android.
 */
const NumberSlider = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  prefix,
  suffix,
  hardMax,
  decimals = 0,
}: Props) => {
  // Local text state so users can clear the field and retype without
  // the controlled value snapping back mid-edit.
  const [text, setText] = useState(() => formatNum(value, decimals));
  const focusedRef = useRef(false);
  useEffect(() => {
    // Don't clobber what the user is typing.
    if (focusedRef.current) return;
    setText(formatNum(value, decimals));
  }, [value, decimals]);

  const ceil = hardMax ?? max * 100;
  const sliderValue = Math.min(max, Math.max(min, value));

  const commit = (raw: string) => {
    const cleaned = raw.replace(/,/g, "").trim();
    if (cleaned === "") {
      onChange(min);
      return;
    }
    const n = Number(cleaned);
    if (!Number.isFinite(n)) {
      setText(formatNum(value, decimals));
      return;
    }
    const clamped = Math.min(ceil, Math.max(min, n));
    onChange(decimals === 0 ? Math.round(clamped) : Math.round(clamped * 10) / 10);
  };

  return (
    <div className="py-2" style={{ touchAction: "pan-y" }}>
      <div className="flex items-center justify-between mb-1.5 gap-3">
        <label className="text-sm font-semibold text-foreground/90">{label}</label>
        <div className="flex items-center gap-1 rounded-lg border border-border bg-background px-2 py-1 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/30 transition-colors">
          {prefix && (
            <span className="text-xs text-muted-foreground select-none">{prefix}</span>
          )}
          <input
            type="text"
            inputMode={decimals === 0 ? "numeric" : "decimal"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => {
              focusedRef.current = true;
            }}
            onBlur={(e) => {
              focusedRef.current = false;
              commit(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            className="w-24 bg-transparent text-end text-sm font-bold tabular-nums text-foreground outline-none"
            aria-label={label}
          />
          {suffix && (
            <span className="text-xs text-muted-foreground select-none">{suffix}</span>
          )}
        </div>
      </div>
      <Slider
        value={[sliderValue]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        aria-label={label}
      />
    </div>
  );
};

function formatNum(n: number, decimals: number) {
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: 0,
    useGrouping: true,
  }).format(n);
}

export default NumberSlider;
